import { MDXProvider } from '@mdx-js/react'
import css from '@styled-system/css'
import { PageProps, graphql } from 'gatsby'
import React, { ReactNode } from 'react'
import { getImage, IGatsbyImageData } from 'gatsby-plugin-image'
import { Helmet } from 'react-helmet'
import Layout from '../components/Layout'
import { Sidebar, proseLinkStyles } from '../components/podcast/Sidebar'
import { SocialShare } from '../components/podcast/SocialShare'
import { Transcript } from '../components/podcast/Transcript'
import { VisuallyHidden } from '../components/podcast/VisuallyHidden'
import Box from '../components/primitives/Box'
import { siteUrl } from '../components/podcast/constants'
import defaultSocialImage from '../assets/images/podcast/default-social.png'
import logoImage from '../assets/images/unblocked/logo.svg'
import { addSuffix } from '../seoUtils'
import { responsiveBackgroundImage, responsiveScale } from '../styles/helpers'
import { MDXRenderer } from 'gatsby-plugin-mdx'
import { GraphQLEpisode } from '../components/podcast/types'
import Button from '../components/system/Button'
import Block from '../components/primitives/Block'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const HeadMeta: React.FC<{
  frontmatter: GraphQLEpisode['frontmatter']
}> = ({ frontmatter }) => {
  const { guest } = frontmatter

  const url = siteUrl + frontmatter.slug
  const title = addSuffix(`${guest!.name}, ${guest!.company}`)
  const description = frontmatter.meta.description

  const imageData = frontmatter?.meta?.image?.childImageSharp?.original
  const image = siteUrl + (imageData?.src || defaultSocialImage)

  const imageWidth = String(imageData?.width)
  const imageHeight = String(imageData?.height)

  return (
    <Helmet title={title}>
      <meta name="description" content={description} />
      <meta name="image" content={image} />
      <meta property="og:url" content={url} />
      <meta
        property="og:title"
        content={frontmatter.meta?.ogTitle || frontmatter.title}
      />
      <meta property="og:type" content="article" />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content={imageWidth || '1200'} />
      <meta property="og:image:height" content={imageHeight || '628'} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:title"
        content={frontmatter.meta?.ogTitle || frontmatter.title}
      />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  )
}

export default function EpisodeTemplate({
  data,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
PageProps<any>) {
  const { frontmatter, body } = data.episode
  const backgroundImage = getImage(data.backgroundImage)!
  return (
    <Layout isNew variant="dark">
      <HeadMeta frontmatter={frontmatter} />
      <PodcastHeroBlock backgroundImage={backgroundImage} />
      <Box
        maxWidth={1200}
        marginX="auto"
        paddingX={24}
        paddingTop={responsiveScale(32)}
        paddingBottom={32}
      >
        <Button
          href="/podcast/#episodes"
          variant="primary"
          size="medium"
          backgroundColor="podcastBeige"
          color="black"
          hoverProps={{ color: 'black100', backgroundColor: 'podcastBlue' }}
        >
          ← &nbsp; All episodes
        </Button>
      </Box>
      <Box maxWidth={1200} marginX="auto" paddingX={24} paddingBottom={92}>
        <Box font="small" color="black500" paddingBottom={32}>
          {frontmatter.date} · Episode {frontmatter.number}
        </Box>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={48}
        >
          <Box flex={1}>
            <Box.h2 font="h2" paddingBottom={16}>
              {frontmatter.title}
            </Box.h2>
            <Box paddingBottom={16} font="large">
              {frontmatter.meta.description}
            </Box>
            <Box paddingBottom={32}>
              <SocialShare text={frontmatter.title} />
            </Box>
            <Box.iframe
              src={frontmatter.audioUrl}
              width="100%"
              frameBorder="0"
              scrolling="no"
              //@ts-ignore -- This does work
              height={{ xs: 102, lg: 163 }}
              maxWidth={{ xs: 769, lg: '100%' }}
              borderRadius={8}
              css={`
                border: 1px solid #dcdcdc;
                overflow: hidden;
              `}
            />
            <Box paddingTop={32} paddingBottom={12} display="flex" gap={16}>
              <FancyLink href="#transcript">Transcript</FancyLink>
            </Box>
            <Box
              paddingTop={24}
              font="large"
              css={`
                h3 {
                  font-size: 1em;
                  font-weight: 700;
                  margin-top: 1.5em;
                  margin-bottom: 0.25em;
                }

                .anchor.before {
                  display: none;
                }

                ul {
                  padding-left: 18px;
                }

                li {
                  margin-top: 0.125em;
                  margin-bottom: 0.125em;
                }

                p,
                ul {
                  margin-bottom: 24px;
                }

                p:last-child,
                ul:last-child {
                  margin-bottom: 0;
                }

                ${proseLinkStyles}
              `}
              id="notes"
            >
              <VisuallyHidden>
                <h2>Show notes</h2>
              </VisuallyHidden>
              <MDXProvider components={{ Transcript }}>
                <MDXRenderer>{body}</MDXRenderer>
              </MDXProvider>
            </Box>
          </Box>
          <Box
            flexGrow={0}
            flexShrink={1}
            width={{ xs: '100%', md: 300, lg: 330 }}
          >
            <Sidebar relatedEpisodes={data.relatedEpisodes.nodes} />
          </Box>
        </Box>
      </Box>
    </Layout>
  )
}

export const pageQuery = graphql`
  query ($id: String!) {
    episode: mdx(id: { eq: $id }) {
      body
      frontmatter {
        date(formatString: "MMM D, YYYY")
        slug
        title
        audioUrl
        number
        guest {
          name
          company
        }
        meta {
          description
          ogTitle
          image {
            childImageSharp {
              original {
                src
                width
                height
              }
            }
          }
        }
      }
    }
    relatedEpisodes: allMdx(
      sort: { order: DESC, fields: [frontmatter___date] }
      limit: 3
      filter: {
        fileAbsolutePath: { regex: "/content/podcast/episodes/" }
        id: { ne: $id }
      }
    ) {
      nodes {
        frontmatter {
          date(formatString: "MMM D, YYYY")
          slug
          title
          audioUrl
          number
          minutes
        }
      }
    }
    backgroundImage: file(relativePath: { eq: "images/unblocked/hero.png" }) {
      childImageSharp {
        gatsbyImageData(layout: FULL_WIDTH, quality: 99)
      }
    }
  }
`
function FancyLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <Box.a
      href={href}
      font="large"
      fontWeight={500}
      css={css({
        borderBottom: '2px solid',
        borderBottomColor: 'podcastOrange',
        ':hover': {
          color: 'podcastOrange',
        },
      })}
    >
      {children}
    </Box.a>
  )
}

function PodcastHeroBlock({
  backgroundImage,
}: {
  backgroundImage: IGatsbyImageData
}) {
  return (
    <Box
      // This is used by Header to change the color when scrolling past the header.
      className="hero-block"
      backgroundPosition="center"
      backgroundRepeat="no-repeat"
      backgroundSize="cover"
      display="flex"
      alignItems="center"
      css={`
        ${responsiveBackgroundImage(
          backgroundImage.images.fallback?.srcSet || '',
          backgroundImage.backgroundColor || '',
        )}
      `}
    >
      <Block paddingTop={64} paddingBottom={32}>
        <Box marginTop={32} maxWidth={540}>
          <Box.img
            height={84}
            src={logoImage}
            maxHeight="100%"
            transition="width 0.5s ease"
          />
        </Box>
      </Block>
    </Box>
  )
}
